import { FC, useMemo } from "react";
import { Box } from "@mui/material";
import { useAlarmColor } from "../../../../../../../../hooks/use-alarm-color";
import { ITrainControlsResponse } from "../../interfaces";
import { useBlinkAnimation } from "../use-blink-animation";

interface ITiSevenSegmentAnalogProps {
  control: ITrainControlsResponse;
  flashUnacknowledgedAlarms: boolean;
  flashFaultAlarms: boolean;
}

const SEGMENTS: [number, number, number, number][] = [
  [5, 3, 14, 4],
  [18, 5, 4, 12],
  [18, 21, 4, 12],
  [5, 33, 14, 4],
  [2, 21, 4, 12],
  [2, 5, 4, 12],
  [5, 18, 14, 4],
];

const DIGIT_SEG: Record<string, boolean[]> = {
  "0": [true, true, true, true, true, true, false],
  "1": [false, true, true, false, false, false, false],
  "2": [true, true, false, true, true, false, true],
  "3": [true, true, true, true, false, false, true],
  "4": [false, true, true, false, false, true, true],
  "5": [true, false, true, true, false, true, true],
  "6": [true, false, true, true, true, true, true],
  "7": [true, true, true, false, false, false, false],
  "8": [true, true, true, true, true, true, true],
  "9": [true, true, true, true, false, true, true],
  "-": [false, false, false, false, false, false, true],
  " ": [false, false, false, false, false, false, false],
};

const COL_DIM = "#333333";
const COL_ON = "#ffffff";
const COL_BG = "#000000";

const VB_W = 24;
const VB_H = 40;

type DisplayChar = {
  char: string;
  weight: number;
};

function buildDisplayChars(value: string | null): DisplayChar[] {
  const sanitizedValue = (value ?? "").trim();

  if (!sanitizedValue) {
    return [{ char: " ", weight: 1 }];
  }

  return sanitizedValue.split("").map(char => ({
    char,
    weight: char === "." || char === "," ? 0.35 : 1,
  }));
}

const Digit: FC<{
  ch: string;
  digitId: string;
}> = ({ ch, digitId }) => {
  const pattern = DIGIT_SEG[ch] ?? DIGIT_SEG[" "];
  return (
    <Box
      sx={{
        flex: 1,
        minWidth: "10px",
        alignSelf: "stretch",
        display: "flex",
        flexDirection: "column",
        justifyContent: "flex-end",
        alignItems: "center",
        minHeight: 0,
      }}
    >
      <Box
        component="svg"
        viewBox={`0 0 ${VB_W} ${VB_H}`}
        preserveAspectRatio="xMidYMax meet"
        sx={{
          display: "block",
          width: "100%",
          height: "100%",
          maxWidth: "100%",
          maxHeight: "100%",
          overflow: "hidden",
        }}
        aria-hidden
      >
        {SEGMENTS.map(([x, y, w, h], i) => {
          const fill = pattern[i] ? COL_ON : COL_DIM;
          return <rect key={`${digitId}-${i}`} x={x} y={y} width={w} height={h} rx={2} ry={2} fill={fill} />;
        })}
      </Box>
    </Box>
  );
};

const Separator: FC<{
  separator: "." | ",";
}> = ({ separator }) => (
  <Box
    sx={{
      flex: 1,
      minWidth: "4px",
      alignSelf: "stretch",
      display: "flex",
      flexDirection: "column",
      justifyContent: "flex-end",
      alignItems: "center",
      pb: separator === "," ? "1px" : "3px",
      position: "relative",
    }}
  >
    <Box
      component="span"
      sx={{
        width: "5px",
        height: "5px",
        minWidth: "3px",
        minHeight: "3px",
        borderRadius: "1px",
        backgroundColor: COL_ON,
        flexShrink: 0,
      }}
    />
    {separator === "," && (
      <Box
        component="span"
        sx={{
          position: "absolute",
          bottom: "-1px",
          width: "2px",
          height: "7px",
          backgroundColor: COL_ON,
          transform: "rotate(18deg)",
          borderRadius: "1px",
        }}
      />
    )}
  </Box>
);

export const TiSevenSegmentAnalog: FC<ITiSevenSegmentAnalogProps> = ({
  control,
  flashUnacknowledgedAlarms,
  flashFaultAlarms,
}) => {
  const { value, props, alarmStatus, unacknowledgedEvent } = control;

  const alarmTint = useAlarmColor(Number(alarmStatus) || 0);
  const shouldBlinkBg = flashUnacknowledgedAlarms && unacknowledgedEvent > 0;
  const shouldBlinkBorder = flashFaultAlarms && unacknowledgedEvent === 2;
  const { animation, keyframes } = useBlinkAnimation(shouldBlinkBg, shouldBlinkBorder, alarmTint);

  const displayChars = useMemo(() => buildDisplayChars(value), [value]);

  return (
    <Box
      sx={{
        position: "absolute",
        left: props.Left,
        top: props.Top,
        width: props.Width,
        height: props.Height,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: COL_BG,
        border: alarmStatus > 0 ? `2px solid ${alarmTint}` : "1px solid #1a1a1a",
        boxSizing: "border-box",
        overflow: "hidden",
        minWidth: 0,
        minHeight: 0,
        animation,
        ...keyframes,
      }}
    >
      <Box
        sx={{
          width: "100%",
          height: "100%",
          minWidth: 0,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          px: "6px",
          py: "6px",
          boxSizing: "border-box",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            alignItems: "flex-end",
            justifyContent: "center",
            gap: "3px",
            maxWidth: "100%",
            flexShrink: 0,
            width: "100%",
          }}
        >
          {displayChars.map((item, index) => (
            <Box
              key={`${control.idSchemeObject}-${index}-${item.char}`}
              sx={{
                flex: `${item.weight} ${item.weight} 0`,
                minWidth: item.weight < 1 ? "4px" : "10px",
                display: "flex",
                alignItems: "stretch",
              }}
            >
              {item.char === "." || item.char === "," ? (
                <Separator separator={item.char} />
              ) : (
                <Digit ch={item.char} digitId={`${control.idSchemeObject}-${index}`} />
              )}
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
};
